// Drop-in-Ersatz für Reacts lazy() mit Chunk-Load-Retry.
//
// Warum: Strato-Apache (mod_http2) beendet idle HTTP/2-Verbindungen nach ~3s
// mit GOAWAY(Code 70007 = APR_TIMEUP). Requests, die genau in dieses Race
// fallen, scheitern im Browser mit net::ERR_HTTP2_PROTOCOL_ERROR — Edge/Chrome
// wiederholen sie NICHT (GOAWAY-Code != NO_ERROR). Typischer Treffer: die
// Login-Seite ist ein paar Sekunden offen (alle gepoolten Verbindungen laufen
// serverseitig aus), nach dem Klick lädt die SPA mehrere Route-Chunks →
// einzelne schlagen fehl → "Loading CSS chunk N failed" → App crasht.
// Diagnose-Session: 2026-07-05.
//
// Strategie: NUR echte Chunk-LADE-Fehler behandeln (webpack 4 setzt
// err.name="ChunkLoadError", mini-css-extract err.code="CSS_CHUNK_LOAD_FAILED";
// beide in der gebauten Runtime verifiziert). Modul-EVAL-Fehler sofort
// durchwerfen — ein Retry würde dort Webpacks angebrochenen Modul-Cache
// (partial exports) zurückliefern und den echten Fehler maskieren.
// 1. stiller Retry nach kurzer Pause (frische Verbindung → heilt das
// GOAWAY-Race). 2. Scheitert auch der: automatischer Reload, gedrosselt auf
// 1x/Minute über einen WRITE-VERIFIZIERTEN sessionStorage-Zeitstempel (kann
// der Browser den Stempel nicht persistieren — Quota/Private-Mode —, wird NIE
// auto-reloadet: kein Schutz = kein Reload, sonst droht eine Reload-Schleife).
// Der Reload deckt zusätzlich "stale index.html nach Deploy" ab. Webpack 4
// räumt fehlgeschlagene Einträge aus installedChunks/installedCssChunks auf →
// ein erneutes import() lädt wirklich neu (Runtime-verifiziert).

import { lazy as reactLazy } from "react";

const RELOAD_STAMP_KEY = "mm_chunk_reload_at";
const RETRY_DELAY_MS = 400;
const RELOAD_THROTTLE_MS = 60000;

const isChunkLoadError = (err) =>
  !!err && (err.name === "ChunkLoadError" || err.code === "CSS_CHUNK_LOAD_FAILED");

const mayAutoReload = () => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_STAMP_KEY)) || 0;
    return Date.now() - last > RELOAD_THROTTLE_MS;
  } catch (e) {
    return false;
  }
};

// true nur, wenn der Drossel-Stempel nachweislich persistiert wurde —
// sonst wäre der Reload-Loop-Schutz wirkungslos (setItem kann scheitern,
// während getItem funktioniert: Quota voll, alte iOS-Private-Modes).
const markAutoReload = () => {
  try {
    const stamp = String(Date.now());
    sessionStorage.setItem(RELOAD_STAMP_KEY, stamp);
    return sessionStorage.getItem(RELOAD_STAMP_KEY) === stamp;
  } catch (e) {
    return false;
  }
};

export const lazy = (importFn) =>
  reactLazy(() =>
    importFn().catch((firstErr) => {
      if (!isChunkLoadError(firstErr)) throw firstErr;
      return new Promise((resolve) => {
        setTimeout(resolve, RETRY_DELAY_MS);
      })
        .then(importFn)
        .catch((retryErr) => {
          if (isChunkLoadError(retryErr) && mayAutoReload() && markAutoReload()) {
            window.location.reload();
            // Seite lädt ohnehin neu — Promise offen lassen, nichts mehr rendern
            return new Promise(() => {});
          }
          throw retryErr;
        });
    })
  );
